export function newThreadId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Sidebar: subject list shows date + local time (newest already first from API). */
export function formatThreadTimestamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function newAssistantTurnId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function fileExtLabel(filename) {
  const ext = String(filename || "")
    .split(".")
    .pop()
    ?.toLowerCase();
  if (!ext || ext === String(filename || "").toLowerCase()) return "FILE";
  if (ext.length > 4) return "FILE";
  return ext.toUpperCase();
}

export function initialLoadingSteps(_userQuery) {
  return ["▸ Planning next moves…"];
}

/** Upsert live progress / heartbeat lines instead of stacking history. */
export function coalesceHeartbeatVisibleSteps(steps) {
  if (!Array.isArray(steps) || steps.length < 2) {
    return Array.isArray(steps) ? steps : [];
  }
  const isLive = (s) => isLiveProgressStep(String(s || ""));
  const out = [];
  for (const step of steps) {
    if (isLive(step)) {
      const lastIdx = [...out].map((s) => isLive(s)).lastIndexOf(true);
      if (lastIdx >= 0) {
        out[lastIdx] = step;
        continue;
      }
    }
    out.push(step);
  }
  return out;
}

const _LIVE_PROGRESS_BODY_RE =
  /^(still |taking longer|planning next moves|searching|thinking|composing answer|composing\b)/i;

export function stripProgressStepBody(step) {
  return String(step || "")
    .replace(/^[▸✓✗○]\s+/, "")
    .replace(/…?\s*\d+\s*s\s*$/, "")
    .replace(/…+$/, "")
    .trim();
}

export function isLiveProgressStep(step) {
  const body = stripProgressStepBody(step);
  return _LIVE_PROGRESS_BODY_RE.test(body);
}

/** Cursor-style single-line status for the loading panel header. */
export function resolveLiveProgressLabel(steps, elapsedSec = 0) {
  const list = Array.isArray(steps) ? steps : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const raw = String(list[i] || "");
    if (!raw.startsWith("▸ ")) continue;
    const body = stripProgressStepBody(raw);
    if (!body || isApproachSectionHeader(raw)) continue;
    if (_LIVE_PROGRESS_BODY_RE.test(body) || /^searching /i.test(body)) {
      const elapsed =
        elapsedSec >= 2 ? ` · ${formatLoadingElapsedLabel(elapsedSec)}` : "…";
      return `${body}${elapsed}`;
    }
  }
  return formatLoadingElapsedLabel(elapsedSec);
}

/** Client-side loading timer label (ticks even when SSE is silent). */
export function formatLoadingElapsedLabel(elapsedSec) {
  const sec = Math.max(0, Number(elapsedSec) || 0);
  if (sec < 1) return "Working…";
  if (sec < 60) return `${sec}s`;
  const mins = Math.floor(sec / 60);
  const rem = sec % 60;
  return rem ? `${mins}m ${rem}s` : `${mins}m`;
}

export function isInvestigationApproach(steps) {
  if (!Array.isArray(steps) || !steps.length) return false;
  return steps.some((s) => {
    const t = String(s || "")
      .replace(/^[▸✓✗○]\s+/, "")
      .trim();
    return (
      /^Understood\b/i.test(t) ||
      /^Open items\b/i.test(t) ||
      /^Approach\b/i.test(t) ||
      /^Perfect graph route\b/i.test(t) ||
      /^If the graph is incomplete\b/i.test(t) ||
      /^Steps\b/i.test(t) ||
      /^Outcome\b/i.test(t) ||
      /^Prior lock was\b/i.test(t)
    );
  });
}

export function isApproachSectionHeader(step) {
  const t = String(step || "")
    .replace(/^[▸✓✗○]\s+/, "")
    .trim();
  return /^(Understood|Open items|Approach|Steps|Outcome)\b/i.test(t) && !t.includes(":");
}

export function buildQaIncidentBundleV1({
  turnId,
  threadId,
  userQuery,
  assistantAnswer,
  metadata,
}) {
  const meta = metadata || {};
  const mm = meta.metamodel_summary;
  const mmStr = mm != null ? String(mm) : "";
  const maxMm = 8000;
  return {
    identity: {
      schema_version: "1.0",
      turn_id: turnId,
      thread_id: meta.thread_id || threadId || null,
      trace_id: meta.trace_id || null,
      client_build:
        (typeof process !== "undefined" &&
          process.env &&
          process.env.REACT_APP_VERSION) ||
        "ea-assistant-web",
    },
    user_turn: {
      user_query: userQuery || "",
      assistant_answer: assistantAnswer || "",
    },
    api_path: "/api/rag/v2/query",
    rag_response_echo: {
      success: meta.success,
      clarification_needed: meta.clarification_needed,
      sql: meta.sql ?? null,
      thinking: meta.thinking ?? null,
      execution_results: meta.execution_results ?? null,
      data_quality_warnings: meta.data_quality_warnings ?? null,
      visible_steps: meta.visible_steps ?? null,
      query_focus_entity: meta.query_focus_entity ?? null,
      orientation_context: meta.orientation_context ?? null,
      intent_elicitation: meta.intent_elicitation ?? null,
      suggested_follow_ups: meta.suggested_follow_ups ?? null,
      conversation_state: meta.conversation_state ?? null,
      metamodel_summary:
        mmStr.length > maxMm ? mmStr.slice(0, maxMm) : mmStr,
      metamodel_summary_truncated: mmStr.length > maxMm,
    },
    tool_trace: [],
    errors: meta.error
      ? [{ phase: "client_message", message: String(meta.error) }]
      : [],
    hypothesis_tags: [],
    repro_hint: null,
  };
}
